import { supabase } from '../lib/supabase'
import {
  COMMISSION_GST_RATE, CommissionTermsSource, commissionEntitlement, commissionTerms,
  netMarginOf, resaleOf, termsLabel,
} from '../lib/margin'
import { ALL_TIME, DateRange, inRange } from '../lib/dateRange'
import { treatmentOf, gstComponent } from '../lib/gstTreatment'
import { Deal, TeamMember, DealAttribution, CommissionInvoice } from '../types'
import { ACQUIRED_STATUSES, SIGNED_STATUSES, SOLD_STATUSES } from '../lib/dealLifecycle'

// ==========================================================================
// ADMIN CRUD — team members, attributions, commission invoices, capitalised costs
// (backs the Commissions admin screens)
// ==========================================================================

const vLabel = (d: Deal) =>
  `${(d.autograb_year ?? d.vehicle_year) ?? ''} ${d.autograb_make || d.vehicle_make || ''} ${d.autograb_model || d.vehicle_model || ''}`.trim()
const vVin = (d: Deal) => d.autograb_vin || d.vehicle_vin || ''

// ---- Team members ----

export async function listTeamMembers(): Promise<TeamMember[]> {
  const { data, error } = await supabase.from('team_members').select('*').order('name')
  if (error) throw error
  return (data || []) as TeamMember[]
}

export async function saveTeamMember(m: {
  id?: string; name: string; email?: string; role: string; commission_basis?: string;
  commission_rate: number; commission_amount?: number; risk_rating_days: number; active?: boolean
}): Promise<void> {
  const basis = m.commission_basis === 'fixed' ? 'fixed' : 'percent'
  const payload = {
    name: m.name, email: m.email || null, role: m.role,
    commission_basis: basis,
    /* Both columns are written whichever basis is chosen, and the one that is not in use is
       zeroed rather than left behind. A stale 25% sitting under a fixed fee is the kind of
       thing that gets read by a report a year later and paid. */
    commission_rate: basis === 'percent' ? m.commission_rate : 0,
    commission_amount: basis === 'fixed' ? (m.commission_amount || 0) : 0,
    risk_rating_days: m.risk_rating_days,
    active: m.active ?? true,
  }
  const { error } = m.id
    ? await supabase.from('team_members').update(payload).eq('id', m.id)
    : await supabase.from('team_members').insert([payload])
  if (error) throw error
}

export async function setTeamMemberActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from('team_members').update({ active }).eq('id', id)
  if (error) throw error
}

// ---- Deal attributions ----

export interface AttributionRow {
  id: string
  dealId: string
  dealLabel: string
  memberId: string
  memberName: string
  role: string
  /** Percent basis only; 0 on a fixed-fee attribution. */
  rate: number
  /** "25%" or "$500 fixed" — what this deal was credited at. */
  termsLabel: string
  createdAt: string
}

export async function listAttributions(): Promise<AttributionRow[]> {
  const [a, d, m] = await Promise.all([
    supabase.from('deal_attributions').select('*').order('created_at', { ascending: false }),
    supabase.from('deals').select('*').is('deleted_at', null),
    supabase.from('team_members').select('*'),
  ])
  for (const r of [a, d, m]) if (r.error) throw r.error
  const byId = new Map((d.data || []).map((x: any) => [x.id, x as Deal]))
  const memById = new Map((m.data || []).map((x: any) => [x.id, x as TeamMember]))
  return (a.data || []).map((x: any): AttributionRow => ({
    id: x.id,
    dealId: x.deal_id,
    dealLabel: byId.has(x.deal_id) ? vLabel(byId.get(x.deal_id)!) : x.deal_id.slice(0, 8),
    memberId: x.team_member_id,
    memberName: memById.get(x.team_member_id)?.name || '—',
    role: x.role,
    rate: Number(x.rate),
    termsLabel: termsLabel(commissionTerms(x)),
    createdAt: x.created_at,
  }))
}

export interface DealBrief { id: string; label: string; vin: string }

export async function listDealsBrief(): Promise<DealBrief[]> {
  const { data, error } = await supabase.from('deals').select('*').is('deleted_at', null).order('created_at', { ascending: false })
  if (error) throw error
  return (data || []).map((d: any) => ({ id: d.id, label: vLabel(d as Deal), vin: vVin(d as Deal) }))
}

export async function saveAttribution(a: {
  deal_id: string; team_member_id: string; role: string; rate: number
  basis?: string; fixed_amount?: number
}): Promise<void> {
  const basis = a.basis === 'fixed' ? 'fixed' : 'percent'
  const { error } = await supabase
    .from('deal_attributions')
    .upsert(
      [{
        deal_id: a.deal_id,
        team_member_id: a.team_member_id,
        role: a.role,
        /* The snapshot now carries the basis as well as the number, so a deal credited
           under a flat fee stays on that fee even if the member moves to a percentage
           next month. Snapshotting the rate alone would have silently re-based it. */
        basis,
        rate: basis === 'percent' ? a.rate : 0,
        fixed_amount: basis === 'fixed' ? (a.fixed_amount || 0) : 0,
      }],
      { onConflict: 'deal_id,team_member_id,role' },
    )
  if (error) throw error
}

/**
 * Update an existing attribution by its id.
 *
 * Deliberately not `saveAttribution`. That one upserts on the composite key
 * (deal_id, team_member_id, role), so changing the member or the role on an
 * existing row would insert a *second* attribution and leave the original in
 * place — the deal would then be credited twice. An edit has to be keyed on the
 * row's own id.
 *
 * The rate is re-snapshotted from the member being credited, which is the same
 * rule the create path follows.
 */
export async function updateAttribution(
  id: string,
  a: { deal_id: string; team_member_id: string; role: string; rate: number; basis?: string; fixed_amount?: number }
): Promise<void> {
  // Guard the composite key by hand, since an id-keyed update bypasses it.
  const { data: clash, error: clashError } = await supabase
    .from('deal_attributions')
    .select('id')
    .eq('deal_id', a.deal_id)
    .eq('team_member_id', a.team_member_id)
    .eq('role', a.role)
    .neq('id', id)
    .maybeSingle()
  if (clashError) throw clashError
  if (clash) {
    throw new Error('That team member is already credited on this deal in that role.')
  }

  const { error } = await supabase
    .from('deal_attributions')
    .update({
      deal_id: a.deal_id,
      team_member_id: a.team_member_id,
      role: a.role,
      basis: a.basis === 'fixed' ? 'fixed' : 'percent',
      rate: a.basis === 'fixed' ? 0 : a.rate,
      fixed_amount: a.basis === 'fixed' ? (a.fixed_amount || 0) : 0,
    })
    .eq('id', id)
  if (error) throw error
}

export async function deleteAttribution(id: string): Promise<void> {
  const { error } = await supabase.from('deal_attributions').delete().eq('id', id)
  if (error) throw error
}

// ---- Commission invoices (with byte-match fingerprint) ----

export interface CommInvoiceRow {
  id: string
  invoiceRef: string
  memberId: string | null
  memberName: string
  dealId: string | null
  dealLabel: string
  amount: number
  receivedAt: string | null
  status: string
  notes: string
  /** The uploaded invoice, when one was attached. Null on a line logged without a file. */
  filePath: string | null
  fileName: string
}

const COMMISSION_INVOICE_BUCKET = 'commission-invoices'

/**
 * Store the invoice document and hand back where it landed.
 *
 * Winnie, 13 Sep, asked for drag and drop on this log. Storing the file is the point of
 * that: the log records what is owed, and until now the document behind each line lived in
 * somebody's email. The bucket is private; the file is read back through a signed URL.
 */
export async function uploadCommissionInvoiceFile(file: File): Promise<{ path: string; name: string; type: string }> {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  const safe = file.name.replace(/[^A-Za-z0-9._-]/g, '_').slice(-80)
  const path = stamp + '-' + safe
  const { data, error } = await supabase.storage.from(COMMISSION_INVOICE_BUCKET).upload(path, file, { upsert: false })
  if (error) throw error
  return { path: data ? data.path : path, name: file.name, type: file.type || '' }
}

/** A link that opens a logged commission invoice. Null when the line has no document. */
export async function commissionInvoiceFileUrl(filePath: string | null, seconds = 300): Promise<string | null> {
  if (!filePath) return null
  const { data, error } = await supabase.storage.from(COMMISSION_INVOICE_BUCKET).createSignedUrl(filePath, seconds)
  if (error) throw error
  return data?.signedUrl || null
}

/** SHA-256 fingerprint of the invoice content (payee|amount|content). */
export async function fingerprintOf(payeeId: string, amount: number | string, content: string): Promise<string> {
  const raw = `${payeeId}|${amount}|${(content || '').trim()}`
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw))
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 48)
}

export async function listCommissionInvoicesAdmin(): Promise<CommInvoiceRow[]> {
  const [ci, d, m] = await Promise.all([
    supabase.from('commission_invoices').select('*').order('received_at', { ascending: false }),
    supabase.from('deals').select('*').is('deleted_at', null),
    supabase.from('team_members').select('*'),
  ])
  for (const r of [ci, d, m]) if (r.error) throw r.error
  const byId = new Map((d.data || []).map((x: any) => [x.id, x as Deal]))
  const memById = new Map((m.data || []).map((x: any) => [x.id, x as TeamMember]))
  return (ci.data || []).map((x: any): CommInvoiceRow => ({
    id: x.id,
    invoiceRef: x.invoice_ref,
    memberId: x.team_member_id,
    memberName: x.team_member_id ? memById.get(x.team_member_id)?.name || 'Unknown' : 'Unknown',
    dealId: x.deal_id,
    dealLabel: x.deal_id && byId.has(x.deal_id) ? vLabel(byId.get(x.deal_id)!) : '—',
    amount: Number(x.amount),
    receivedAt: x.received_at,
    status: x.status,
    notes: x.notes || '',
    filePath: x.file_path || null,
    fileName: x.file_name || '',
  }))
}

/** Log an invoice; detects duplicates by fingerprint and blocks them. Returns {duplicate}. */
export async function saveCommissionInvoice(inv: {
  invoice_ref: string; team_member_id?: string | null; deal_id?: string | null; amount: number
  received_at?: string; notes?: string; content?: string
  file_path?: string | null; file_name?: string | null; file_type?: string | null
}): Promise<{ duplicate: boolean }> {
  const fingerprint = await fingerprintOf(inv.team_member_id || 'none', inv.amount, inv.content || inv.notes || '')
  const { data: existing, error: qErr } = await supabase
    .from('commission_invoices').select('id').eq('fingerprint', fingerprint).limit(1)
  if (qErr) throw qErr
  const duplicate = (existing || []).length > 0
  const { error } = await supabase.from('commission_invoices').insert([{
    invoice_ref: inv.invoice_ref,
    team_member_id: inv.team_member_id || null,
    deal_id: inv.deal_id || null,
    amount: inv.amount,
    fingerprint,
    file_path: inv.file_path || null,
    file_name: inv.file_name || null,
    file_type: inv.file_type || null,
    received_at: inv.received_at || undefined,
    status: duplicate ? 'blocked' : 'received',
    notes: duplicate ? `Byte-match duplicate. ${inv.notes || ''}`.trim() : (inv.notes || null),
  }])
  if (error) throw error
  return { duplicate }
}

export async function deleteCommissionInvoice(id: string): Promise<void> {
  const { error } = await supabase.from('commission_invoices').delete().eq('id', id)
  if (error) throw error
}

// ---- Capitalised costs ----

export interface CostRow {
  id: string
  label: string
  vin: string
  buyIn: number
  transport: number
  repairs: number
  totalCapitalised: number
  landedCost: number
}

export async function listDealsForCosts(): Promise<CostRow[]> {
  const { data, error } = await supabase.from('deals').select('*').is('deleted_at', null).order('created_at', { ascending: false })
  if (error) throw error
  return (data || []).map((d: any): CostRow => {
    const buyIn = Number(d.valuation_amount || 0)
    const transport = Number(d.capitalised_transport || 0)
    const repairs = Number(d.capitalised_repairs || 0)
    const cap = transport + repairs
    // Landed cost nets off the GST that is actually claimable on the
    // acquisition, which depends on the seller's GST status (§4.4) — not a flat
    // eleventh of every purchase.
    const claimable = treatmentOf(d as Deal) === 'no_gst' ? 0 : gstComponent(buyIn)
    return { id: d.id, label: vLabel(d as Deal), vin: vVin(d as Deal), buyIn, transport, repairs, totalCapitalised: cap, landedCost: buyIn + cap - claimable }
  })
}

export async function saveCapitalisedCosts(dealId: string, transport: number, repairs: number): Promise<void> {
  const { error } = await supabase
    .from('deals')
    .update({ capitalised_transport: transport, capitalised_repairs: repairs, updated_at: new Date().toISOString() })
    .eq('id', dealId)
  if (error) throw error
}

/**
 * Phase 5 — Commission / attribution service.
 * Backs the Sales Performance and Commission Reconciliation reports.
 *
 * Entitlement model (Framework Part): a team member credited on a deal earns
 * `rate` × the deal's gross profit — spotter 25%, buyer-sourcer 50%.
 * Reconciliation matches invoices received from payees against those
 * calculated entitlements and flags variances, duplicates, and unmatched items.
 */

const dYear = (d: Deal) => d.autograb_year ?? d.vehicle_year
const dMake = (d: Deal) => d.autograb_make || d.vehicle_make || ''
const dModel = (d: Deal) => d.autograb_model || d.vehicle_model || ''
const vehicleLabel = (d: Deal) => `${dYear(d) ?? ''} ${dMake(d)} ${dModel(d)}`.trim()

// Net margin is defined once, in src/lib/margin.ts. This alias keeps the
// existing call sites reading the way they always have.
const profitOf = netMarginOf

const shortId = (id: string) => (id.length > 8 ? id.slice(0, 8).toUpperCase() : id)

/* Deals counted as "closed" for performance: every one the money has gone out on.
   This was a local list that had never been told about deal_pending, sold_in_transit
   or sold_delivered, so a rep's sale disappeared from their numbers the moment the
   car moved to one of them. */
const REALIZED = ACQUIRED_STATUSES
/* The terms rather than the rate, since 13 Sep: "$500 fixed" reads as plainly as "25%"
   and a fixed-fee payee shown as "0%" would look like a bug. */
const roleLabel = (role: string, terms: CommissionTermsSource) =>
  `${role === 'buyer_sourcer' ? 'Buyer-Sourcer' : role === 'sales' ? 'Sales' : 'Spotter'} ${termsLabel(commissionTerms(terms))}`

function electionPath(d: Deal): 'path1' | 'path2' | 'path3' {
  // v2.0-NL.2.0 removed the deposit, so the three election paths collapse to
  // two: lodgement elected, or not.
  if (d.lodgement_elected) return 'path1'
  return 'path3'
}

interface Bundle {
  deals: Deal[]
  members: TeamMember[]
  attributions: DealAttribution[]
  invoices: any[]
  commInvoices: CommissionInvoice[]
  /* Freight jobs, for the transit-days figure Winnie asked for on 13 Sep. Keyed by deal. */
  freight: Map<string, { created_at: string | null; delivered_at: string | null; status: string }>
  byId: Map<string, Deal>
  memberById: Map<string, TeamMember>
}

async function loadBundle(): Promise<Bundle> {
  const [d, m, a, inv, ci, fr] = await Promise.all([
    supabase.from('deals').select('*').is('deleted_at', null),
    supabase.from('team_members').select('*').order('name'),
    supabase.from('deal_attributions').select('*'),
    supabase.from('invoices').select('*'),
    supabase.from('commission_invoices').select('*'),
    supabase.from('freight_out_workspaces').select('deal_id, created_at, delivered_at, status'),
  ])
  for (const r of [d, m, a, inv, ci]) if (r.error) throw r.error
  /* Deliberately not in the throw list above. The freight table is new, and a report that
     refuses to render because one optional column of one optional table could not be read
     would be a worse failure than a blank transit figure. */
  const deals = (d.data || []) as Deal[]
  const members = (m.data || []) as TeamMember[]
  return {
    deals,
    members,
    attributions: (a.data || []) as DealAttribution[],
    invoices: inv.data || [],
    commInvoices: (ci.data || []) as CommissionInvoice[],
    freight: new Map(
      ((fr.data || []) as any[]).map((x) => [
        String(x.deal_id),
        { created_at: x.created_at || null, delivered_at: x.delivered_at || null, status: String(x.status || '') },
      ]),
    ),
    byId: new Map(deals.map((x) => [x.id, x])),
    memberById: new Map(members.map((x) => [x.id, x])),
  }
}

// ==========================================================================
// SALES PERFORMANCE
// ==========================================================================

/** The eleven KPIs §6.8 asks for, per team member. */
export interface MemberKpis {
  opportunitiesCreated: number
  offersMade: number
  offerAcceptanceRate: number | null
  avgPurchaseMargin: number
  avgDaysLeadToPayment: number | null
  avgDaysPaymentToReceived: number | null
  awaitingDocuments: number
  awaitingPayment: number
  awaitingTransport: number
  stockPurchased: number
  avgAcquisitionCost: number
  /**
   * Mean days from a freight job opening to the vehicle being delivered, over this
   * member's deals in the period. Winnie, 13 Sep: it "flags whether a team member's deals
   * are picking slow carriers or under-quoting freight in a way that causes delivery
   * delays." Null where none of their deals has been delivered yet - and null means not
   * measured, which is why it is not reported as zero.
   *
   * Only Direct Auto freight can have a transit period; a buyer collecting their own car
   * has none, so those deals are outside this figure rather than counted as instant.
   */
  avgTransitDays: number | null
  /** How many of their delivered deals the figure above rests on. */
  transitDeals: number
  /**
   * The mix of seller types they work, as percentages of their sourced deals.
   *
   * Winnie, 13 Sep: Type 3/4/5 "skips most of the acquisition workflow... so a team member
   * working mostly Type 3/4/5 deals will show inflated conversion and speed numbers
   * compared to someone doing harder Type 1/2 private-seller work. This isn't a rate to
   * optimise - it's context that makes the other rates comparable across people."
   */
  sellerTypeMix: { type1: number; type2: number; type345: number; deals: number }
  /* The three conversion rates Winnie's 13 Sep team-summary layout asks for, beside the
     accept rate that was already here. Each is null rather than 0 when its denominator is
     empty: nobody made any offers is a different fact from every offer was refused. */
  offerRate: number | null
  accToPaidRate: number | null
  sourcedToSoldRate: number | null
}

export interface MemberPerformance {
  memberId: string
  name: string
  role: string
  /** Percent basis only; 0 for a member on a flat fee. */
  rate: number
  /** "25%" or "$500 fixed" — what this person is paid on, in one label. */
  termsLabel: string
  riskDays: number
  dealsClosed: number
  grossResale: number
  grossProfit: number
  marginPct: number
  /** Their share of realised net margin, exclusive of GST. */
  commission: number
  /** GST added on the end — 10% of `commission`. */
  commissionGst: number
  /** What they invoice: `commission` + `commissionGst`. */
  commissionPayable: number
  /** The negative part of `commission` — what losses took off the total (§6.1). */
  lossShare: number
  /** §6.8 */
  kpis: MemberKpis
  breakEvenDeals: number
  belowBreakEven: number
  lossCount: number
  lossValue: number
  arAvgDays: number | null
  pathMix: { path1: number; path2: number; path3: number }
  arInvoices: number
  weakerTermsCount: number
  weakerTermsValue: number
}

export interface SalesPerformanceReport {
  stats: {
    dealsClosed: number
    grossResale: number
    grossProfit: number
    commission: number
    /** The headline tile on Winnie's layout: of what the team sourced, how much has sold. */
    sourcedToSold: number | null
  }
  members: MemberPerformance[]
}

/**
 * §6.8 period views.
 *
 * The team summary had no date filter at all — it was always all-time, which
 * made "how did we go this month" unanswerable from this screen.
 */
export type KpiPeriod = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'all'

export const KPI_PERIOD_LABEL: Record<KpiPeriod, string> = {
  day: 'Today',
  week: 'This week',
  month: 'This month',
  quarter: 'This quarter',
  year: 'This year',
  all: 'All Time',
}

export function periodStart(period: KpiPeriod, now = new Date()): Date | null {
  const d = new Date(now)
  switch (period) {
    case 'day':
      d.setHours(0, 0, 0, 0)
      return d
    case 'week': {
      // Monday as the first day, which is how a dealership talks about a week.
      const day = (d.getDay() + 6) % 7
      d.setDate(d.getDate() - day)
      d.setHours(0, 0, 0, 0)
      return d
    }
    case 'month':
      return new Date(d.getFullYear(), d.getMonth(), 1)
    case 'quarter':
      return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)
    case 'year':
      return new Date(d.getFullYear(), 0, 1)
    default:
      return null
  }
}

export async function loadSalesPerformance(
  period: KpiPeriod = 'all',
  range: DateRange = ALL_TIME
): Promise<SalesPerformanceReport> {
  const b = await loadBundle()
  const since = periodStart(period)
  const presetPeriod = (iso?: string | null) =>
    !since || (!!iso && new Date(iso).getTime() >= since.getTime())

  // An explicit range wins over the preset chips. Empty is all of time, which is
  // what the chips meant by "all", so nothing moves until a window is chosen.
  const hasRange = Boolean(range.from || range.to)
  const inPeriod = (iso?: string | null) =>
    hasRange ? inRange(iso, range) : presetPeriod(iso)

  // Index invoices by deal for AR cycle / terms deviation.
  const invByDeal = new Map<string, any[]>()
  for (const inv of b.invoices) {
    const list = invByDeal.get(inv.deal_id) || []
    list.push(inv)
    invByDeal.set(inv.deal_id, list)
  }

  const members: MemberPerformance[] = b.members.map((tm) => {
    const attrs = b.attributions.filter((a) => a.team_member_id === tm.id)
    const deals = attrs.map((a) => b.byId.get(a.deal_id)).filter(Boolean) as Deal[]
    /* Terms, not a bare rate: a member can be on a flat fee per deal since 13 Sep. The
       attribution's snapshot wins; the member's current terms are only the fallback. */
    const termsOf = (dealId: string) =>
      commissionTerms(attrs.find((a) => a.deal_id === dealId) ?? tm)
    const rateOf = (dealId: string) => termsOf(dealId).rate

    // §6.8. Everything below is scoped to the chosen period; a deal counts
    // toward a stage's KPI on the date it reached that stage, not on the date it
    // was created, or a car bought in June would keep appearing in July's
    // "offers made".
    const created = deals.filter((d) => inPeriod(d.created_at))
    const offersMade = deals.filter((d) => inPeriod(d.bill_of_sale_sent_at))
    const offersAccepted = offersMade.filter((d) =>
      SIGNED_STATUSES.includes(
        d.deal_status
      )
    )
    const purchased = deals.filter((d) => inPeriod(d.paid_at))
    const avgPurchaseMargin = purchased.length
      ? purchased.reduce((s2, d) => s2 + (Number(d.gross_margin) || 0), 0) / purchased.length
      : 0
    const avgAcquisitionCost = purchased.length
      ? purchased.reduce(
          (s2, d) =>
            s2 +
            (Number(d.valuation_amount) || 0) +
            (Number(d.capitalised_transport) || 0) +
            (Number(d.capitalised_repairs) || 0),
          0
        ) / purchased.length
      : 0

    const daysBetween = (a?: string | null, z?: string | null) =>
      a && z ? (new Date(z).getTime() - new Date(a).getTime()) / 86_400_000 : null

    const leadToPayment = purchased
      .map((d) => daysBetween(d.created_at, d.paid_at))
      .filter((n): n is number => n !== null)
    const paymentToReceived = deals
      .filter((d) => inPeriod(d.sold_at))
      .map((d) => daysBetween(d.paid_at, d.sold_at))
      .filter((n): n is number => n !== null)

    const mean = (xs: number[]) => (xs.length ? xs.reduce((a, x) => a + x, 0) / xs.length : null)

    const awaitingDocs = deals.filter(
      (d) => d.deal_status === 'contract_signed' && d.dd_status !== 'approved'
    ).length
    const awaitingPayment = deals.filter((d) =>
      ['committed', 'approved_for_payment'].includes(d.deal_status)
    ).length
    // Awaiting transport: sold, Direct Auto is carrying it, and no carrier has
    // been quoted yet. There is no delivery-confirmed flag to measure against,
    // so this counts what has not yet been arranged rather than what is in
    // transit — the number that actually needs someone to act.
    const awaitingTransport = deals.filter(
      (d) =>
        // In transit is precisely the stage that is awaiting transport; delivered is not.
        ['sold', 'sold_in_transit', 'awaiting_payment'].includes(d.deal_status) &&
        (d.transport_arranged_by ?? 'direct_auto') === 'direct_auto' &&
        !d.transport_quoted_at
    ).length

    /* Transit speed, per Winnie's 13 Sep note and Sale Details spec section 10: from the
       freight job opening to the car being delivered. Measured on the freight record rather
       than on the deal, because that is where the delivery is actually recorded - the deal
       has no delivered date at all. */
    const transitDays: number[] = []
    for (const d of deals) {
      const f = b.freight.get(d.id)
      if (!f || f.status !== 'Delivered' || !f.delivered_at || !f.created_at) continue
      if (!inPeriod(f.delivered_at)) continue
      const days = (new Date(f.delivered_at).getTime() - new Date(f.created_at).getTime()) / 86_400_000
      if (Number.isFinite(days) && days >= 0) transitDays.push(days)
    }

    /* Seller-type mix over the deals they sourced in the period. Types 3, 4 and 5 are
       reported together because they share one workflow - the licensed-seller fast track -
       and it is that shortcut, not the difference between a dealer and an auction, that
       makes the other numbers on this row easier to earn. */
    const sourcedInPeriod = deals.filter((d) => inPeriod(d.created_at))
    const countType = (match: (t: string) => boolean) =>
      sourcedInPeriod.filter((d) => match(String(d.seller_type || 'individual'))).length
    const mixTotal = sourcedInPeriod.length
    const asPct = (n: number) => (mixTotal ? Math.round((n / mixTotal) * 100) : 0)

    /* Conversion, measured at each step rather than only end to end. A rate with nothing
       under it is null, not zero - see the interface. */
    const paidFromAccepted = offersAccepted.filter((d) => inPeriod(d.paid_at)).length
    const soldFromCreated = created.filter((d) => SOLD_STATUSES.includes(d.deal_status)).length
    const rate = (num: number, den: number) => (den ? Math.round((num / den) * 1000) / 10 : null)

    const kpis: MemberKpis = {
      opportunitiesCreated: created.length,
      offersMade: offersMade.length,
      offerAcceptanceRate: offersMade.length
        ? Math.round((offersAccepted.length / offersMade.length) * 100)
        : null,
      avgPurchaseMargin,
      avgDaysLeadToPayment: mean(leadToPayment),
      avgDaysPaymentToReceived: mean(paymentToReceived),
      awaitingDocuments: awaitingDocs,
      awaitingPayment,
      awaitingTransport,
      stockPurchased: purchased.length,
      avgAcquisitionCost,
      avgTransitDays: mean(transitDays),
      transitDeals: transitDays.length,
      offerRate: rate(offersMade.length, created.length),
      accToPaidRate: rate(paidFromAccepted, offersAccepted.length),
      sourcedToSoldRate: rate(soldFromCreated, created.length),
      sellerTypeMix: {
        type1: asPct(countType((t) => t === 'individual')),
        type2: asPct(countType((t) => t === 'company')),
        type345: asPct(countType((t) => t === 'dealer' || t === 'auction' || t === 'tender')),
        deals: mixTotal,
      },
    }

    const realized = deals
      .filter((d) => REALIZED.includes(d.deal_status))
      .filter((d) => inPeriod(d.sold_at || d.paid_at || d.created_at))
    const grossResale = realized.reduce((s, d) => s + resaleOf(d), 0)
    const grossProfit = realized.reduce((s, d) => s + profitOf(d), 0)
    // §6.1: a loss is shared at the same rate, as a deduction. Flooring losses
    // at zero — which is what happened before — quietly meant Direct Auto wore
    // every bad deal alone while paying out on every good one.
    const commission = realized.reduce((s, d) => s + commissionEntitlement(profitOf(d), termsOf(d.id)).net, 0)
    /* A loss is shared at the same rate on the percentage basis. A flat fee does not move
       with the margin, so a fixed-fee deal contributes nothing to the loss share - it is
       still payable in full, and it is already counted in `commission` above. */
    const lossShare = realized
      .filter((d) => profitOf(d) < 0 && termsOf(d.id).basis === 'percent')
      .reduce((s, d) => s + profitOf(d) * rateOf(d.id), 0)
    const lossDeals = deals.filter((d) => profitOf(d) < 0)

    // AR cycle: avg (payment_received_at − invoice_date) over their deals' invoices.
    const cycles: number[] = []
    let weakerCount = 0
    let weakerValue = 0
    let arInvoices = 0
    for (const d of deals) {
      for (const inv of invByDeal.get(d.id) || []) {
        arInvoices++
        if (inv.invoice_date && inv.payment_received_at) {
          cycles.push((new Date(inv.payment_received_at).getTime() - new Date(inv.invoice_date).getTime()) / 86_400_000)
        }
        if (inv.invoice_date && inv.due_date) {
          const terms = Math.round((new Date(inv.due_date).getTime() - new Date(inv.invoice_date).getTime()) / 86_400_000)
          if (terms > tm.risk_rating_days) {
            weakerCount++
            weakerValue += Number(inv.total_amount || 0)
          }
        }
      }
    }
    const arAvgDays = cycles.length ? cycles.reduce((s, n) => s + n, 0) / cycles.length : null

    const paths = { path1: 0, path2: 0, path3: 0 }
    for (const d of deals) paths[electionPath(d)]++
    const total = deals.length || 1
    const pathMix = {
      path1: Math.round((paths.path1 / total) * 100),
      path2: Math.round((paths.path2 / total) * 100),
      path3: Math.round((paths.path3 / total) * 100),
    }

    return {
      memberId: tm.id,
      name: tm.name,
      role: tm.role,
      rate: tm.commission_rate,
      termsLabel: termsLabel(commissionTerms(tm)),
      riskDays: tm.risk_rating_days,
      dealsClosed: realized.length,
      grossResale,
      grossProfit,
      marginPct: grossResale ? (grossProfit / grossResale) * 100 : 0,
      commission,
      commissionGst: Math.round(commission * COMMISSION_GST_RATE * 100) / 100,
      commissionPayable: Math.round(commission * (1 + COMMISSION_GST_RATE) * 100) / 100,
      lossShare,
      kpis,
      breakEvenDeals: realized.filter((d) => profitOf(d) === 0).length,
      belowBreakEven: realized.filter((d) => profitOf(d) < 0).length,
      lossCount: lossDeals.length,
      lossValue: lossDeals.reduce((s, d) => s + profitOf(d), 0),
      arAvgDays,
      pathMix,
      arInvoices,
      weakerTermsCount: weakerCount,
      weakerTermsValue: weakerValue,
    }
  })

  return {
    stats: {
      dealsClosed: members.reduce((s, m) => s + m.dealsClosed, 0),
      grossResale: members.reduce((s, m) => s + m.grossResale, 0),
      grossProfit: members.reduce((s, m) => s + m.grossProfit, 0),
      commission: members.reduce((s, m) => s + m.commission, 0),
      /* Team-wide, computed from the deals rather than averaged from the per-person rates:
         an average of percentages weights a person with three deals the same as one with
         thirty. Null when nothing was sourced in the period. */
      sourcedToSold: (() => {
        const sourced = b.deals.filter((d) => inPeriod(d.created_at))
        if (!sourced.length) return null
        const sold = sourced.filter((d) => SOLD_STATUSES.includes(d.deal_status)).length
        return Math.round((sold / sourced.length) * 1000) / 10
      })(),
    },
    members,
  }
}

// ==========================================================================
// COMMISSION RECONCILIATION
// ==========================================================================

export interface EntitlementLine {
  dealId: string
  vehicle: string
  memberId: string
  payee: string
  feeType: string
  /** The share itself: net margin x rate, exclusive of GST. */
  calculated: number
  /** GST added on the end — 10% of `calculated`. */
  gst: number
  /** What the payee actually invoices: `calculated` + `gst`. */
  payable: number
}

export interface ReconInvoice {
  id: string
  dealId: string | null
  vehicle: string
  payee: string
  feeType: string
  invoiceRef: string
  amount: number
  calculated: number | null
  gst: number | null
  payable: number | null
  variance: number | null
  outcome: 'matched' | 'overcharge' | 'variance' | 'duplicate' | 'unmatched'
  matchesRef?: string
}

export interface PayeeSummary {
  memberId: string
  payee: string
  role: string
  calculated: number
  gst: number
  payable: number
  invoiced: number
  variance: number
  status: 'matched' | 'overcharge' | 'variance' | 'held'
}

export interface CommissionReconReport {
  stats: { calculated: number; gst: number; payable: number; matched: number; heldVariance: number; heldOther: number }
  summary: PayeeSummary[]
  matched: ReconInvoice[]
  exceptions: ReconInvoice[]
  duplicates: ReconInvoice[]
  unmatched: ReconInvoice[]
}

export async function loadCommissionRecon(
  range: DateRange = ALL_TIME
): Promise<CommissionReconReport> {
  const loaded = await loadBundle()

  // A range on a reconciliation means two dates, not one: an entitlement belongs
  // to the period its vehicle SOLD in, and a payee's invoice to the period it was
  // RECEIVED in. Asking for "commission for August" means both. It is done here
  // rather than by trimming rows on the screen because the totals, the variances
  // and the held amounts all have to move with the window — a filter that only
  // hides rows would leave a total that no longer adds up to what is shown.
  const b: Bundle = {
    ...loaded,
    attributions: loaded.attributions.filter((a) => {
      const deal = loaded.byId.get(a.deal_id)
      return inRange(deal ? deal.sale_date || deal.sold_at || null : null, range)
    }),
    commInvoices: loaded.commInvoices.filter((inv) => inRange((inv as any).received_at, range)),
  }

  // Calculated entitlements per (deal, member) from attributions.
  const entitlements: EntitlementLine[] = b.attributions.map((a) => {
    const d = b.byId.get(a.deal_id)
    const tm = b.memberById.get(a.team_member_id)
    return {
      dealId: a.deal_id,
      vehicle: d ? vehicleLabel(d) : shortId(a.deal_id),
      memberId: a.team_member_id,
      payee: tm?.name || '—',
      feeType: roleLabel(a.role, a),
      // Matches the engine: a loss is a negative entitlement, not a zero one,
      // so recon reconciles against what is actually payable.
      calculated: d ? commissionEntitlement(profitOf(d), commissionTerms(a)).net : 0,
      gst: d ? commissionEntitlement(profitOf(d), commissionTerms(a)).gst : 0,
      payable: d ? commissionEntitlement(profitOf(d), commissionTerms(a)).payable : 0,
    }
  })
  const entByKey = new Map(entitlements.map((e) => [`${e.dealId}|${e.memberId}`, e]))

  // Duplicate detection: byte-match fingerprint, else (deal|member|amount).
  const seen = new Map<string, CommissionInvoice>()
  const dupIds = new Set<string>()
  const sortedInv = [...b.commInvoices].sort((a, b2) => (a.received_at || '').localeCompare(b2.received_at || ''))
  for (const inv of sortedInv) {
    const key = inv.fingerprint || `${inv.deal_id}|${inv.team_member_id}|${inv.amount}`
    if (inv.status === 'blocked' || seen.has(key)) dupIds.add(inv.id)
    else seen.set(key, inv)
  }
  const firstOfKey = (inv: CommissionInvoice) =>
    seen.get(inv.fingerprint || `${inv.deal_id}|${inv.team_member_id}|${inv.amount}`)

  const rows: ReconInvoice[] = b.commInvoices.map((inv) => {
    const d = inv.deal_id ? b.byId.get(inv.deal_id) : undefined
    const tm = inv.team_member_id ? b.memberById.get(inv.team_member_id) : undefined
    const ent = inv.deal_id && inv.team_member_id ? entByKey.get(`${inv.deal_id}|${inv.team_member_id}`) : undefined
    // The payee invoices GST-inclusive, so that is the figure the invoice is
    // checked against. The ex-GST share and the GST ride alongside it so the
    // report can show how the number was built.
    const calc = ent ? ent.payable : null
    const variance = calc === null ? null : Number((inv.amount - calc).toFixed(2))

    let outcome: ReconInvoice['outcome']
    if (dupIds.has(inv.id)) outcome = 'duplicate'
    else if (calc === null) outcome = 'unmatched'
    else if (Math.abs(variance!) < 1) outcome = 'matched'
    else outcome = variance! > 0 ? 'overcharge' : 'variance'

    return {
      id: inv.id,
      dealId: inv.deal_id || null,
      vehicle: d ? vehicleLabel(d) : (inv.deal_id ? shortId(inv.deal_id) : '—'),
      payee: tm?.name || '—',
      feeType: ent?.feeType || (tm ? roleLabel(tm.role, tm) : '—'),
      invoiceRef: inv.invoice_ref,
      amount: Number(inv.amount),
      calculated: ent ? ent.calculated : null,
      gst: ent ? ent.gst : null,
      payable: calc,
      variance,
      outcome,
      matchesRef: outcome === 'duplicate' ? firstOfKey(inv)?.invoice_ref : undefined,
    }
  })

  const matched = rows.filter((r) => r.outcome === 'matched')
  const exceptions = rows.filter((r) => r.outcome === 'overcharge' || r.outcome === 'variance')
  const duplicates = rows.filter((r) => r.outcome === 'duplicate')
  const unmatched = rows.filter((r) => r.outcome === 'unmatched')

  // Per-payee summary.
  const byMember = new Map<string, PayeeSummary>()
  for (const e of entitlements) {
    const tm = b.memberById.get(e.memberId)
    const s = byMember.get(e.memberId) || {
      memberId: e.memberId, payee: e.payee, role: tm?.role || 'spotter',
      calculated: 0, gst: 0, payable: 0, invoiced: 0, variance: 0, status: 'matched' as const,
    }
    s.calculated += e.calculated
    s.gst += e.gst
    s.payable += e.payable
    byMember.set(e.memberId, s)
  }
  for (const inv of b.commInvoices) {
    if (!inv.team_member_id) continue
    const tm = b.memberById.get(inv.team_member_id)
    const s = byMember.get(inv.team_member_id) || {
      memberId: inv.team_member_id, payee: tm?.name || '—', role: tm?.role || 'spotter',
      calculated: 0, gst: 0, payable: 0, invoiced: 0, variance: 0, status: 'matched' as const,
    }
    if (!dupIds.has(inv.id)) s.invoiced += Number(inv.amount)
    byMember.set(inv.team_member_id, s)
  }
  const summary: PayeeSummary[] = [...byMember.values()].map((s) => {
    s.variance = Number((s.invoiced - s.payable).toFixed(2))
    const memberRows = rows.filter((r) => b.memberById.get(s.memberId)?.name === r.payee)
    if (memberRows.some((r) => r.outcome === 'duplicate' || r.outcome === 'unmatched')) s.status = 'held'
    else if (Math.abs(s.variance) < 1) s.status = 'matched'
    else s.status = s.variance > 0 ? 'overcharge' : 'variance'
    return s
  }).sort((a, b2) => b2.calculated - a.calculated)

  return {
    stats: {
      calculated: entitlements.reduce((s, e) => s + e.calculated, 0),
      gst: entitlements.reduce((s, e) => s + e.gst, 0),
      payable: entitlements.reduce((s, e) => s + e.payable, 0),
      matched: matched.reduce((s, r) => s + r.amount, 0),
      heldVariance: exceptions.reduce((s, r) => s + Math.abs(r.variance || 0), 0),
      heldOther: duplicates.reduce((s, r) => s + r.amount, 0) + unmatched.reduce((s, r) => s + r.amount, 0),
    },
    summary,
    matched,
    exceptions,
    duplicates,
    unmatched,
  }
}
